import React, { useState } from 'react';
import { Modal } from '@cmhc-schl/hearth-ui';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ErrorMessage, { SpinnerElement } from '../ErrorMessage';
import { usePortfolioCards } from './hooks/usePortfolioCards';
import PortfolioList from './PortfolioList';
import styles from './styles.module.scss';
import getModalContent, { ModalType } from './utils/ModalSwitcher';
import { statusMapper } from './utils/PortfolioStatusMapper';
import { PortfolioNotificationConfig } from './types';

interface PortfolioCardProps {
  portfolioIds: number[];
  onNotificationBanner?: (config: PortfolioNotificationConfig | null) => void;
}

const PortfolioCard: React.FC<PortfolioCardProps> = ({ portfolioIds, onNotificationBanner }) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const { data: portfolios, loading, error } = usePortfolioCards({ portfolioIds });
  const { getLabel } = statusMapper(t);

  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalResolver, setModalResolver] = useState<((result: boolean) => void) | null>(null);
  const [confirmCallback, setConfirmCallback] = useState<(() => void | Promise<void>) | undefined>(
    undefined
  );

  const openModal = (type: ModalType, onConfirm?: () => void | Promise<void>): Promise<boolean> => {
    return new Promise((resolve) => {
      setModalType(type);
      setIsModalOpen(true);
      setConfirmCallback(() => onConfirm);
      setModalResolver(() => resolve);
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleModalPrimaryClick = async () => {
    if (confirmCallback) {
      await confirmCallback();
      setIsModalOpen(false);
    }
    setIsModalOpen(false);
    modalResolver?.(true);
    setModalResolver(null);
    setConfirmCallback(undefined);
  };

  const handleModalDismiss = () => {
    setIsModalOpen(false);
    modalResolver?.(false);
    setModalResolver(null);
    setConfirmCallback(undefined);
  };

  const modalData = modalType
    ? getModalContent(modalType, t, () => handleModalPrimaryClick())
    : null;

  const renderContent = () => {
    if (loading) {
      return <SpinnerElement />;
    }

    if (!loading && error?.length > 0) {
      return <ErrorMessage errorType={error} />;
    }

    return (
      <>
        <Modal
          id={modalData?.id ?? '1'}
          isActive={isModalOpen}
          onDismissalCallback={closeModal}
          title={{ value: modalData?.title }}
          bodyContent={modalData?.bodyContent}
          {...(modalData?.modalButtons && {
            modalButtons: {
              ...modalData.modalButtons,
              primaryButton: {
                ...modalData.modalButtons.primaryButton,
                onClick: handleModalPrimaryClick
              },
              secondaryButton: {
                ...modalData.modalButtons.secondaryButton,
                onClick: handleModalDismiss
              }
            }
          })}
          shouldCloseOnBackgroundClick={true}
        />

        <PortfolioList
          portfolios={portfolios}
          getLabel={getLabel}
          t={t}
          onOpenModal={openModal}
          onNotificationBanner={onNotificationBanner}
        />
      </>
    );
  };

  return (
    <>
      <div className={styles.portfolioCardContainer}>
        <Typography className={styles.header} variant="h1">
          <div className={styles.portfolioHeading}>{t('Portfolios')}</div>
          <div className={styles.portfolioCardLoading}>
            {loading
              ? t('Loading-Items')
              : portfolios?.length === 1
                ? t('Items_0', { count: portfolios?.length })
                : t('Item', {
                    count: portfolios?.length
                  })}
          </div>
        </Typography>

        {renderContent()}
      </div>
    </>
  );
};

export default PortfolioCard;
